import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import type { MouseEvent, ReactNode } from "react";
import { Link as RouterLink, type To } from "react-router";

/**
 * The part of a row that says the row opens something.
 *
 * Rows that open a record were marked as clickable with `cursor: pointer` and nothing else, which
 * is a signal you have to already be hovering to receive. #5122 is what that costs: a user looked
 * at a row with a delete icon on it, found no edit icon beside the delete, and concluded the entry
 * could not be edited — while the row under their cursor opened the editor. The icon column was
 * where they looked because nothing in the row itself said *click me*.
 *
 * The answer is not an edit icon. That would duplicate the row gesture exactly, on the tightest
 * column in the table, and it would do nothing for the eight tables that are clickable and have no
 * actions column at all. Naming the row as a link says the same thing in the place the eye already
 * is, costs no column width, and gives keyboard and screen-reader users a target that a hover
 * cursor never did.
 *
 * ### Link colored at rest, underlined on hover
 *
 * The cue has to show before the reader points at the row, or it reproduces #5122 in a nicer font.
 * The link color is that cue: it reads as a link at rest, beside text that does not (#6711). The
 * underline comes when the pointer is on the link or anywhere on its row, which also opens it, so the
 * row says what a click on it does. That is the list standard of #6707.
 *
 * It is one of three ways into a row, with the row's own hover and the ⋯ that ends the row, whose
 * first item is Open (`RowActions`).
 *
 * ### A route gets an anchor, a dialog gets a button
 *
 * Pass `to` where the row navigates and the link is a real `<a href>`: middle-click, ctrl-click and
 * "open in new tab" all work, which they never did on a row `onClick`. Pass `onClick` where the row
 * opens a dialog and there is no URL to point at; that renders a button styled the same way, which
 * is focusable and keyboard-operable even though it has nowhere to point. Passing neither renders
 * plain text, which is what a row that cannot be opened — a load issue, a skeleton — should get.
 */
const RowLinkText = styled(Link)(({ theme }) => ({
  // `component="button"` renders a real <button>, which arrives with a user-agent font, padding and
  // border that a table cell should not inherit. Stating them here means the anchor and the button
  // are the same text at the same baseline, which is the whole point of having one component.
  background: "none",
  border: "none",
  color: theme.palette.primary.main,
  cursor: "pointer",
  display: "block",
  font: "inherit",
  maxWidth: "100%",
  overflow: "hidden",
  padding: 0,
  textAlign: "left",
  textDecoration: "none",
  textDecorationColor: theme.palette.primary.main,
  textOverflow: "ellipsis",
  verticalAlign: "baseline",
  whiteSpace: "nowrap",
  "&:hover, &:focus-visible, tr:hover &": { textDecoration: "underline" },
})) as typeof Link;

export default function RowLink(
  props: Readonly<{
    children: ReactNode;
    /** Where the row navigates. Renders a real anchor, so the browser's own link gestures work. */
    to?: To;
    /** What the row opens, where it opens a dialog rather than a route. Renders a button. */
    onClick?: () => void;
    /**
     * Accessible name, where the visible text is not enough on its own — a column of links reading
     * "Untitled" tells a screen reader nothing about which row it is on. Defaults to the text.
     */
    ariaLabel?: string;
  }>,
) {
  const { ariaLabel, children, onClick, to } = props;

  // A row that cannot be opened must not claim it can. Load-issue and skeleton rows reach here with
  // neither destination, and get the text they would have had before this component existed.
  if (to === undefined && onClick === undefined) return <>{children}</>;

  // The row underneath is clickable too and goes to the same place. Letting both fire opens the
  // record twice — harmless for a route, two stacked dialogs for the rest.
  function handleClick(event: MouseEvent) {
    event.stopPropagation();
    onClick?.();
  }

  if (to !== undefined) {
    return (
      <RowLinkText aria-label={ariaLabel} component={RouterLink} to={to} onClick={handleClick}>
        {children}
      </RowLinkText>
    );
  }

  return (
    <RowLinkText aria-label={ariaLabel} component="button" type="button" onClick={handleClick}>
      {children}
    </RowLinkText>
  );
}
